'use client'

/**
 * /aircraft-models — r19.43 Phase 1 (2026-06-24, Stephan-Spec).
 *
 * Public-Page "In der FGL genutzte Modellflugzeuge".
 *
 * Markante Liste aller Basismodelle die mindestens 1 Flug haben, mit
 * aggregierten Σ-Pillen (Stunden, km, Flüge, Maschinen, Piloten) im
 * Wertungs-Glas-Design.
 *
 * Privacy: ausgeloggt sieht nur Σ-Aggregate, keine Piloten-Daten. Die
 * Pilot-Strip ist auf der Detail-Page implementiert (eingeloggt-only).
 */
import { useEffect, useState, useRef } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { FglHeader } from '@/components/layout/fgl-header'
import { Reveal } from '@/components/award'
import { Plane, Clock, Ruler, Users, Wind, Factory, LayoutGrid, List } from 'lucide-react'
import { imageStyleFromPosition } from '@/components/hangar/image-position-editor'

interface ModelStat {
  id: string
  manufacturer: string
  modelName: string
  spanMm: number
  classCode: string
  imageUrl: string | null
  // r19.43.9 (Stephan-Spec): Pilot-Hangar-Foto-Fallback
  firstGalleryUrl: string | null
  // r19.43.15: Position des Galerie-Fallbacks (zoom/x/y) für einheitlichen Crop
  firstGalleryPosition?: { zoom: number; x: number; y: number } | null
  aircraftCount: number
  pilotCount: number
  flightCount: number
  totalDurationSeconds: number
  totalDistanceM: number
  totalScoringM: number
  bestScoringM: number | null
}

type SortField = 'total_seconds' | 'total_distance_m' | 'flight_count' | 'pilot_count' | 'manufacturer' | 'alpha'

const CLASS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  STANDARD: 'Standard',
  CLUBCLASS: 'Club',
}

function fmtHours(seconds: number): string {
  if (seconds <= 0) return '0 h'
  const h = seconds / 3600
  if (h >= 100) return `${Math.round(h)} h`
  if (h >= 10) return `${h.toFixed(1)} h`
  return `${h.toFixed(2)} h`
}

function fmtKm(meters: number): string {
  if (meters <= 0) return '0 km'
  const km = meters / 1000
  if (km >= 10000) return `${Math.round(km).toLocaleString('de-DE')} km`
  if (km >= 100) return `${km.toFixed(0)} km`
  return `${km.toFixed(1)} km`
}

export default function AircraftModelsPage() {
  const [models, setModels] = useState<ModelStat[]>([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState<SortField>('total_seconds')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [classCode, setClassCode] = useState<string>('')
  // r19.43.2 (Stephan-Spec 2026-06-24): Saison-Toggle. Default = current.
  const [season, setSeason] = useState<'current' | 'all'>('current')
  const currentYear = new Date().getFullYear()
  // r19.43.6 (Stephan-Spec): Infinite-Scroll. Initial 10 sichtbar, beim
  // Scrollen ab Sentinel werden weitere eingeblendet (je 1 pro Sekunde
  // animiert über framer-motion staggered delay).
  const [visibleCount, setVisibleCount] = useState(10)
  // r19.46.1 (Stephan-Spec 2026-06-26): Grid/Liste-Ansichts-Toggle.
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  // r19.55.62 (Stephan-Spec 2026-07-14): Polar-Contributor-Attribution
  // pro Modell. Batch-Fetch, damit keine N+1.
  const [polarSummary, setPolarSummary] = useState<Record<string, { sinkPilots: string[]; circlePilots: string[] }>>({})

  useEffect(() => {
    setLoading(true)
    const params = new URLSearchParams({ sortBy, sortOrder, season })
    if (classCode) params.set('classCode', classCode)
    fetch(`/api/public/aircraft-models/stats?${params}`, { cache: 'no-store' })
      .then(r => r.json())
      .then(j => {
        if (j.success) setModels(j.data)
        setLoading(false)
        // Bei Filter/Sort-Wechsel: Sichtbarkeit auf 10 zurücksetzen
        setVisibleCount(10)
      })
      .catch(() => setLoading(false))
  }, [sortBy, sortOrder, classCode, season])

  // r19.55.62: Polar-Summary einmal laden (aendert sich selten, cache-safe)
  useEffect(() => {
    fetch('/api/public/models/polar-summary', { cache: 'no-store' })
      .then(r => r.json())
      .then(j => { if (j.data) setPolarSummary(j.data) })
      .catch(() => { /* silent - Badges verschwinden dann einfach */ })
  }, [])

  // r19.43.6: IntersectionObserver — Sentinel triggert +10 Modelle
  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    if (visibleCount >= models.length) return
    if (loading) return
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisibleCount((c) => Math.min(c + 10, models.length))
        }
      },
      { rootMargin: '300px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [visibleCount, models.length, loading])

  const totalAircraft = models.reduce((s, m) => s + m.aircraftCount, 0)
  const totalPilots = models.reduce((s, m) => s + m.pilotCount, 0) // approximative Summe (kann Piloten doppelt zählen bei Multi-Modell)
  const totalHours = models.reduce((s, m) => s + m.totalDurationSeconds, 0) / 3600
  const totalKm = models.reduce((s, m) => s + m.totalDistanceM, 0) / 1000

  // r19.55.205 GEO: ItemList-JSON-LD fuer alle geladenen Modelle — jedes Modell
  // als ListItem mit Product-Typ, name, brand (manufacturer), url zur Detail-Seite.
  // Google/Bing/AI-Suchen indexieren damit strukturiert.
  const itemListJsonLd = models.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'FGL-Modelle — genutzte Modellsegelflugzeuge',
    description: 'Alle Modellsegelflugzeuge die aktiv in der FREE GLIDE LEAGUE geflogen werden',
    numberOfItems: models.length,
    itemListElement: models.slice(0, 100).map((m, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Product',
        name: `${m.manufacturer} ${m.modelName}`,
        brand: { '@type': 'Brand', name: m.manufacturer },
        category: 'RC Model Glider',
        url: `https://free-glide-league.com/aircraft-models/${m.id}`,
        image: m.imageUrl || undefined,
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Wingspan', value: `${m.spanMm} mm`, unitCode: 'MMT' },
          { '@type': 'PropertyValue', name: 'FGL Class', value: m.classCode },
        ],
      },
    })),
  } : null

  return (
    <div className="min-h-screen" style={{ background: 'var(--award-paper)' }}>
      {itemListJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
        />
      )}
      <FglHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-28">
        {/* Hero */}
        <Reveal>
          <div className="mb-8">
            <div
              className="text-[10px] font-black uppercase tracking-[.28em] mb-3"
              style={{ color: 'var(--accent)' }}
            >
              FGL-MODELL-PARK
            </div>
            <h1
              className="font-black tracking-tight"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(36px, 5.4vw, 72px)',
                lineHeight: 0.95,
                color: 'var(--text)',
                textTransform: 'uppercase',
                letterSpacing: '-0.015em',
                textShadow: '0 0 40px color-mix(in oklch, var(--accent) 22%, transparent)',
              }}
            >
              In der FGL genutzte Modellflugzeuge
            </h1>
            <p className="mt-4 text-sm md:text-base text-[var(--muted)] max-w-3xl leading-relaxed">
              Alle Modelle die in der FREE GLIDE LEAGUE aktiv geflogen werden — mit
              aggregierten Summen über alle Piloten: Flugstunden, Streckenkilometer,
              Anzahl der Maschinen und Anzahl der Piloten. Klick auf ein Modell für
              die Detailseite mit Top-Piloten.
            </p>
          </div>
        </Reveal>

        {/* Globale Σ-Pillen */}
        <Reveal delay={1}>
          <div className="flex flex-wrap items-baseline gap-x-10 gap-y-4 mb-8 pb-8" style={{ borderBottom: '1px solid var(--award-line)' }}>
            <GlobalStat label="Modell-Typen" value={models.length.toString()} unit="" />
            <GlobalStat label="Anzahl der Modelle" value={totalAircraft.toLocaleString('de-DE')} unit="" />
            <GlobalStat label="Σ Stunden" value={Math.round(totalHours).toLocaleString('de-DE')} unit="h" />
            <GlobalStat label="Σ Strecke" value={Math.round(totalKm).toLocaleString('de-DE')} unit="km" />
          </div>
        </Reveal>

        {/* Filter + Sortierung */}
        <Reveal delay={2}>
          <div className="space-y-3 mb-6">
            {/* r19.43.2: Saison-Toggle wie auf /wertung (Stephan-Spec). */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-black opacity-60 mr-1">Zeitraum:</span>
              <FilterPill active={season === 'current'} onClick={() => setSeason('current')} label={`Aktuell in Nutzung ${currentYear}`} />
              <FilterPill active={season === 'all'} onClick={() => setSeason('all')} label="Alle Modelle in den Hangars" />
            </div>
            {/* Klassen-Filter */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-wider font-black opacity-60 mr-1">Klasse:</span>
              <FilterPill active={classCode === ''} onClick={() => setClassCode('')} label="Alle" />
              <FilterPill active={classCode === 'OPEN'} onClick={() => setClassCode('OPEN')} label="Open" />
              <FilterPill active={classCode === 'STANDARD'} onClick={() => setClassCode('STANDARD')} label="Standard" />
              <FilterPill active={classCode === 'CLUBCLASS'} onClick={() => setClassCode('CLUBCLASS')} label="Club" />
            </div>
            {/* Sortier-Pillen */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono tabular-nums text-xs" style={{ color: 'var(--award-cirrus-3)', letterSpacing: '0.12em' }}>
                  {models.length} {models.length === 1 ? 'Modell' : 'Modelle'}
                </span>
                {/* r19.46.1 (Stephan-Spec): Grid / Liste-Toggle */}
                <div className="flex items-center gap-1">
                  <ViewModePill active={viewMode === 'list'} onClick={() => setViewMode('list')} icon={<List className="h-3 w-3" />} label="Liste" />
                  <ViewModePill active={viewMode === 'grid'} onClick={() => setViewMode('grid')} icon={<LayoutGrid className="h-3 w-3" />} label="Grid" />
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-black opacity-60 mr-1">Sortieren:</span>
                <SortPill field="total_seconds" current={sortBy} order={sortOrder} icon={<Clock className="h-3 w-3" />} label="Stunden" onClick={() => toggleSort('total_seconds', sortBy, sortOrder, setSortBy, setSortOrder)} />
                <SortPill field="total_distance_m" current={sortBy} order={sortOrder} icon={<Ruler className="h-3 w-3" />} label="Strecke" onClick={() => toggleSort('total_distance_m', sortBy, sortOrder, setSortBy, setSortOrder)} />
                <SortPill field="pilot_count" current={sortBy} order={sortOrder} icon={<Users className="h-3 w-3" />} label="Piloten" onClick={() => toggleSort('pilot_count', sortBy, sortOrder, setSortBy, setSortOrder)} />
                <SortPill field="flight_count" current={sortBy} order={sortOrder} icon={<Plane className="h-3 w-3" />} label="Flüge" onClick={() => toggleSort('flight_count', sortBy, sortOrder, setSortBy, setSortOrder)} />
                <SortPill field="manufacturer" current={sortBy} order={sortOrder} icon={<Factory className="h-3 w-3" />} label="Hersteller" onClick={() => toggleSort('manufacturer', sortBy, sortOrder, setSortBy, setSortOrder)} />
                <SortPill field="alpha" current={sortBy} order={sortOrder} icon={<Wind className="h-3 w-3" />} label="A-Z" onClick={() => toggleSort('alpha', sortBy, sortOrder, setSortBy, setSortOrder)} />
              </div>
            </div>
          </div>
        </Reveal>

        {/* Modell-Liste */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl h-32 animate-pulse" style={{ background: 'var(--award-ink-2)', border: '1px solid var(--award-line)' }} />
            ))}
          </div>
        ) : models.length === 0 ? (
          <div className="rounded-2xl p-14 text-center" style={{ background: 'var(--award-ink-2)', border: '1px dashed var(--award-line-hi)' }}>
            <Plane className="h-7 w-7 mx-auto mb-3" style={{ color: 'var(--award-gold)' }} />
            <p>Keine Modelle gefunden.</p>
          </div>
        ) : (
          <motion.div
            layout
            className={viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'
              : 'space-y-3'}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              {models.slice(0, visibleCount).map((m, idx) => (
                <motion.div
                  key={m.id}
                  layout
                  initial={{ opacity: 0, y: 14, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{
                    duration: 0.45,
                    ease: [0.22, 1, 0.36, 1],
                    // r19.43.6 (Stephan-Spec): 1 Sekunde Cascade pro Batch
                    // (idx % 10) * 0.1 → max 1.0s pro 10er-Welle. Bei jedem
                    // Sentinel-Trigger startet die nächste Welle frisch.
                    delay: (idx % 10) * 0.1,
                  }}
                >
                  <ModelCard model={m} compact={viewMode === 'grid'} polarInfo={polarSummary[m.id]} />
                </motion.div>
              ))}
            </AnimatePresence>
            {/* Sentinel + Lade-Indikator + Ende-Marker */}
            {visibleCount < models.length && (
              <div ref={sentinelRef} className="flex items-center justify-center py-6 gap-3">
                <div className="h-4 w-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
                <span className="text-xs font-bold uppercase tracking-wider opacity-70">
                  Weitere Modelle laden …
                </span>
              </div>
            )}
            {visibleCount >= models.length && models.length > 10 && (
              <div className="text-center py-6 text-xs font-mono opacity-60" style={{ color: 'var(--award-cirrus-3)' }}>
                — Ende der Liste · {models.length} Modelle —
              </div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  )
}

function toggleSort(
  field: SortField,
  current: SortField,
  order: 'asc' | 'desc',
  setField: (f: SortField) => void,
  setOrder: (o: 'asc' | 'desc') => void,
) {
  if (field === current) {
    setOrder(order === 'desc' ? 'asc' : 'desc')
  } else {
    setField(field)
    setOrder(field === 'alpha' || field === 'manufacturer' ? 'asc' : 'desc')
  }
}

function GlobalStat({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div>
      <div className="text-[11px] font-black uppercase tracking-[.18em] mb-1" style={{ color: '#fb923c' }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span
          className="font-bold tabular-nums"
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'clamp(28px, 3.6vw, 44px)',
            lineHeight: 1,
            color: '#fb923c',
            textShadow: '0 0 24px rgba(251,146,60,.35)',
          }}
        >
          {value}
        </span>
        {unit && <span className="font-mono font-bold text-sm" style={{ color: '#fb923c' }}>{unit}</span>}
      </div>
    </div>
  )
}

function FilterPill({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className="wertung-tab-pill inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      data-state={active ? 'active' : 'inactive'}
    >
      {label}
    </button>
  )
}

function SortPill({ field, current, order, icon, label, onClick }: {
  field: SortField; current: SortField; order: 'asc' | 'desc'; icon: React.ReactNode; label: string; onClick: () => void
}) {
  const isActive = current === field
  return (
    <button
      onClick={onClick}
      className="wertung-tab-pill inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      data-state={isActive ? 'active' : 'inactive'}
    >
      {icon} {label}
      {isActive && <span style={{ fontSize: 9 }}>{order === 'asc' ? '↑' : '↓'}</span>}
    </button>
  )
}

function ViewModePill({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string
}) {
  return (
    <button
      onClick={onClick}
      className="wertung-tab-pill inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
      data-state={active ? 'active' : 'inactive'}
    >
      {icon} {label}
    </button>
  )
}

function ModelCard({
  model,
  compact = false,
  polarInfo,
}: {
  model: ModelStat
  compact?: boolean
  polarInfo?: { sinkPilots: string[]; circlePilots: string[] }
}) {
  const sinkP = polarInfo?.sinkPilots ?? []
  const circP = polarInfo?.circlePilots ?? []
  return (
    <Link
      href={`/aircraft-models/${model.id}`}
      className="block rounded-2xl p-4 md:p-5 transition-all hover:scale-[1.005] h-full"
      style={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        boxShadow: '0 1px 0 0 color-mix(in oklch, var(--accent) 10%, transparent)',
        textDecoration: 'none',
      }}
    >
      <div className={compact
        ? 'flex flex-col gap-3 h-full'
        : 'flex items-start gap-4 flex-wrap md:flex-nowrap'
      }>
        {/* r19.43.9 (Stephan-Spec): Master-Foto vom Hersteller scheitert oft
            (CSP/Hotlink). Fallback aufs erste Pilot-Hangar-Foto.
            r19.43.15: Position durchreichen damit Thumbnail denselben Crop
            zeigt wie Hero + Editor.
            r19.46.1: Grid-Modus = Foto volle Breite oben statt seitlich. */}
        <ModelThumbnail
          src={model.imageUrl}
          fallbackSrc={model.firstGalleryUrl}
          fallbackPosition={model.firstGalleryPosition || null}
          alt={model.modelName}
          fullWidth={compact}
        />

        {/* Titel + Klasse */}
        <div className="flex-1 min-w-0">
          <div className={compact ? 'mb-2' : 'flex items-baseline gap-3 flex-wrap mb-2'}>
            <h3
              className="font-extrabold uppercase tracking-tight"
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: compact ? 'clamp(16px, 1.6vw, 20px)' : 'clamp(18px, 2vw, 26px)',
                lineHeight: 1.1,
                color: 'var(--text)',
              }}
            >
              {model.manufacturer} · {model.modelName}
            </h3>
            <span className={`inline-block text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${compact ? 'mt-1' : ''}`}
              style={{ background: 'color-mix(in oklch, var(--accent) 15%, transparent)', color: 'var(--accent)' }}>
              {CLASS_LABEL[model.classCode] || model.classCode} · {(model.spanMm / 1000).toFixed(2)}m
            </span>
            {/* r19.55.62 (Stephan-Spec 2026-07-14): Polar-Contributor-Badges.
                Nur wenn Piloten beigetragen. Tooltip mit Piloten-Namen. */}
            {sinkP.length > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${compact ? 'mt-1' : ''}`}
                style={{
                  background: 'color-mix(in oklch, #3b82f6 14%, transparent)',
                  color: '#3b82f6',
                  border: '1px solid color-mix(in oklch, #3b82f6 40%, transparent)',
                }}
                data-tip={`Gleitpolare erflogen von: ${sinkP.join(', ')}`}
              >
                📉 Gleitpolare · {sinkP.length}
              </span>
            )}
            {circP.length > 0 && (
              <span
                className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${compact ? 'mt-1' : ''}`}
                style={{
                  background: 'color-mix(in oklch, #14b8a6 14%, transparent)',
                  color: '#14b8a6',
                  border: '1px solid color-mix(in oklch, #14b8a6 40%, transparent)',
                }}
                data-tip={`Kreispolare erflogen von: ${circP.join(', ')}`}
              >
                🌀 Kreispolare · {circP.length}
              </span>
            )}
          </div>

          {/* Σ-Pillen — Wertungs-Glas-Design */}
          <div className={compact
            ? 'grid grid-cols-3 gap-x-3 gap-y-2'
            : 'flex flex-wrap items-baseline gap-x-6 gap-y-2'
          }>
            <CardStat label="Anzahl der Modelle" value={model.aircraftCount.toString()} unit="" />
            <CardStat label="Piloten" value={model.pilotCount.toString()} unit="" />
            <CardStat label="Σ Stunden" value={fmtHours(model.totalDurationSeconds).replace(' h', '')} unit="h" />
            <CardStat label="Σ Strecke" value={fmtKm(model.totalDistanceM).replace(' km', '')} unit="km" />
            <CardStat label="Flüge" value={model.flightCount.toLocaleString('de-DE')} unit="" />
          </div>
        </div>
      </div>
    </Link>
  )
}

function CardStat({ label, value, unit }: { label: string; value: string; unit: string }) {
  return (
    <div>
      <div className="text-[9px] font-black uppercase tracking-[.18em] mb-0.5" style={{ color: '#fb923c' }}>
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-bold tabular-nums" style={{ fontSize: 18, color: '#fb923c', lineHeight: 1 }}>
          {value}
        </span>
        {unit && <span className="font-mono font-bold" style={{ fontSize: 11, color: '#fb923c' }}>{unit}</span>}
      </div>
    </div>
  )
}

/**
 * r19.43.4 (Stephan-Spec 2026-06-24): Thumbnail mit onError-Fallback.
 * Externe Hersteller-Bild-URLs scheitern oft (CSP, Hotlink-Schutz, 404).
 */
function ModelThumbnail({ src, fallbackSrc, fallbackPosition, alt, fullWidth = false }: {
  src: string | null
  fallbackSrc?: string | null
  fallbackPosition?: { zoom: number; x: number; y: number } | null
  alt: string
  fullWidth?: boolean
}) {
  const [primaryFailed, setPrimaryFailed] = useState(false)
  const [fallbackFailed, setFallbackFailed] = useState(false)
  const showPrimary = src && !primaryFailed
  const showFallback = !showPrimary && fallbackSrc && !fallbackFailed
  // r19.43.15: 4:3 statt 3:2 damit Thumbnail denselben Aspect Ratio hat wie
  // Hero + Position-Editor — sonst sieht das positionierte Foto ueberall anders aus.
  // r19.46.1: Grid-Modus = fullWidth (volle Karten-Breite oben).
  return (
    <div
      className={`${fullWidth ? 'w-full' : 'shrink-0'} rounded-xl overflow-hidden flex items-center justify-center`}
      style={{ width: fullWidth ? '100%' : 112, aspectRatio: '4 / 3', background: 'var(--award-ink-2)', border: '1px solid var(--award-line)' }}
    >
      {showPrimary ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src!} alt={alt} className="w-full h-full object-cover" onError={() => setPrimaryFailed(true)} />
      ) : showFallback ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={fallbackSrc!}
          alt={alt}
          className="w-full h-full object-cover"
          style={imageStyleFromPosition(fallbackPosition)}
          onError={() => setFallbackFailed(true)}
        />
      ) : (
        <Plane className="h-7 w-7" style={{ color: 'var(--accent)' }} />
      )}
    </div>
  )
}
